import { translationBaseKey } from '../../../decorators/translation-base-key.decorator';
import { CalculationRunOptions } from '../calculation-run.options';
import {
  advancedFormGroup,
  advancedFormControl,
} from '@magieno/common';
import { CalculationKeynameEnum } from '../../../enums/calculations/calculation-keyname.enum';
import { FieldTypeEnum } from '../../../enums/field-type.enum';
import { FormControlTypeEnum } from '@magieno/common';
import { IsInt, IsNotEmpty, IsOptional, Max, Min } from '@pristine-ts/class-validator';
import { ErrorMessageBuilder } from '../../../builders/error-message.builder';
import { ValidationGroupEnum } from '../../../enums/validation-group.enum';


@translationBaseKey('rrq-options')
export class RrqOptions extends CalculationRunOptions {
  @advancedFormControl({
    type: FormControlTypeEnum.Hidden,
  })
  calculationKeyname: CalculationKeynameEnum = CalculationKeynameEnum.RRQ;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Salaire brut admissible au RRQ',
        en: 'Gross eligible salary for RRQ',
      },
      tooltip: {
        fr: 'Ce champ représente le salaire brut admissible au Régime de rentes du Québec (RRQ). Il s\'agit du montant total du salaire de l\'employé qui est assujetti aux cotisations au RRQ. Le salaire brut admissible est utilisé pour calculer les cotisations au régime de retraite.',
        en: 'This field represents the gross eligible salary for the Quebec Pension Plan (RRQ). It is the total amount of the employee\'s salary that is subject to RRQ contributions. The gross eligible salary is used to calculate contributions to the retirement plan.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  employeesGrossPensionableSalary: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Nombre de périodes de paie dans l\'année',
        en: 'Number of pay periods in the year',
      },
      tooltip: {
        fr: 'Ce champ représente le nombre de périodes de paie dans l\'année pour l\'employé. Il indique combien de fois l\'employé est payé au cours de l\'année. Le nombre de périodes de paie est utilisé pour calculer les cotisations au RRQ sur une base périodique.',
        en: 'This field represents the number of pay periods in the year for the employee. It indicates how many times the employee is paid during the year. The number of pay periods is used to calculate RRQ contributions on a periodic basis.',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  numberOfPayPeriodsInTheYear: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Cotisation de l\'employé au RRQ retenue depuis le début de l\'année',
        en: 'Employee\'s RRQ contributions withheld from the beginning of the year',
      },
      tooltip: {
        fr: 'Ce champ représente le montant total des cotisations au RRQ retenues sur le salaire de l\'employé depuis le début de l\'année. Il indique la somme totale des cotisations déjà prélevées sur le salaire de l\'employé pour le RRQ.',
        en: 'This field represents the total amount of RRQ contributions withheld from the employee\'s salary from the beginning of the year. It indicates the total sum of contributions already deducted from the employee\'s salary for RRQ.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  employeesQPPContributionsWithheld: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Année',
        en: 'Year',
      },
      tooltip: {
        fr: 'Ce champ représente l\'année pour laquelle les cotisations au RRQ sont calculées. Vous pouvez sélectionner l\'année spécifique pour laquelle vous souhaitez effectuer les calculs. Veuillez noter que le RRQ a été introduit en 1966, mais ici, nous limitons les années de calcul à partir de 2020.',
        en: 'This field represents the year for which RRQ contributions are calculated. You can select the specific year for which you want to perform the calculations. Please note that the RRQ was introduced in 1966, but here, we limit the calculation years starting from 2020.',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Select,
    items: [
      {
        label: {
          fr: '2020',
        },
        id: 2020,
      },
      {
        label: {
          fr: '2021',
        },
        id: 2021,
      },
      {
        label: {
          fr: '2022',
        },
        id: 2022,
      },
      {
        label: {
          fr: '2023',
        },
        id: 2023,
      },
      {
        label: {
          fr: '2024',
        },
        id: 2024,
      },
      {
        label: {
          fr: '2025',
        },
        id: 2025,
      },
    ],
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @Min(2020, { buildErrorMessage: ErrorMessageBuilder.build })
  @Max(2025, { buildErrorMessage: ErrorMessageBuilder.build })
  @IsOptional({
    groups: [ValidationGroupEnum.SalaryDividend],
  })
  year: number;
}
